import { el, clear } from "../../utils/dom";
import { getState } from "../../state/store";
import { renderGroups } from "./GroupsBoard";
import type { SeatingChart, Student, Table } from "../../types/models";
import { getTableSize, calculateSeatPositions } from "./SeatingChartEditor";
import { generateInitials, stringToColor } from "@utils/avatar";

// Theme-aware table colors
const TABLE_COLORS_LIGHT = [
    "#e8f4f8", "#f8e8f4", "#f4f8e8", "#f8f4e8",
    "#e8e8f8", "#f8e8e8", "#e8f8e8", "#f4e8f8"
];

const TABLE_COLORS_DARK = [
    "#1a3a4a", "#3a1a3a", "#2a3a1a", "#3a3a1a",
    "#2a2a4a", "#3a2a2a", "#1a3a2a", "#3a1a4a"
];

function getTableColors(): string[] {
    const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? TABLE_COLORS_DARK : TABLE_COLORS_LIGHT;
}

/**
 * Open full-screen presentation mode
 * Shows groups or seating chart in read-only mode with exit button
 */
export function openPresentationMode(): void {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    const mode = cls.settings.mode ?? 'groups';

    // Validate content exists
    if (mode === 'groups' && !cls.lastGroups.length) return;
    if (mode === 'seating') {
        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId) ?? cls.seatingCharts?.[0];
        if (!chart || !chart.lastAssignment) return;
    }

    // Create full-screen overlay
    const overlay = el("div", {
        class: "presentation-overlay",
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: var(--bg); z-index: 2000; overflow: auto; padding: 40px 20px;"
    });

    // Exit button (top-right corner)
    const exitBtn = el("button", {
        class: "presentation-exit primary",
        style: "position: fixed; top: 20px; right: 20px; z-index: 2001; padding: 12px 20px; font-size: 16px;"
    }, "✕ Exit Presentation");

    const close = () => {
        if (document.body.contains(overlay)) {
            document.body.removeChild(overlay);
        }
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("groups:update", handleUpdate);
        document.removeEventListener("seating:update", handleUpdate);
    };

    exitBtn.addEventListener("click", close);

    // Content container
    const contentContainer = el("div", {
        class: "presentation-content",
        style: "max-width: 1400px; margin: 0 auto;"
    });

    // Render content based on mode
    const renderContent = () => {
        clear(contentContainer);
        const newState = getState();
        const newCls = newState.classes.find(c => c.id === s.activeClassId);
        if (!newCls) return;

        if (mode === 'groups') {
            contentContainer.style.display = "grid";
            contentContainer.style.gap = "16px";
            contentContainer.style.gridTemplateColumns = "repeat(auto-fit, minmax(280px, 1fr))";
            renderGroups(contentContainer, newCls, { showControls: false, enableDragDrop: false });
        } else {
            const chart = newCls.seatingCharts?.find(c => c.id === newCls.activeChartId) ?? newCls.seatingCharts?.[0];
            if (chart) {
                contentContainer.style.display = "block";
                contentContainer.appendChild(renderSeatingChartPresentation(chart, newCls.students));
            }
        }
    };

    renderContent();

    // Re-render on updates
    const handleUpdate = renderContent;
    document.addEventListener("groups:update", handleUpdate);
    document.addEventListener("seating:update", handleUpdate);

    // Escape key to exit
    const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            close();
        }
    };
    document.addEventListener("keydown", handleEscape);

    overlay.append(exitBtn, contentContainer);
    document.body.appendChild(overlay);
}

/**
 * Render seating chart for presentation mode (read-only, no editing controls)
 */
function renderSeatingChartPresentation(chart: SeatingChart, students: Student[]): HTMLElement {
    const container = el("div", {
        class: "seating-presentation",
        style: "position: relative;"
    });

    // Title
    const title = el("h2", {
        style: "text-align: center; margin-bottom: 24px; font-size: 1.5rem;"
    }, chart.name);
    container.appendChild(title);

    // Calculate bounds for the chart
    let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
    for (const table of chart.tables) {
        const size = getTableSize(table);
        minX = Math.min(minX, table.x);
        minY = Math.min(minY, table.y);
        maxX = Math.max(maxX, table.x + size.width);
        maxY = Math.max(maxY, table.y + size.height);
    }

    // Add padding
    const padding = 60;
    const chartWidth = maxX - minX + padding * 2;
    const chartHeight = maxY - minY + padding * 2;

    // Chart wrapper with zoom/pan support
    let zoomLevel = 1.0;
    let isPanning = false;
    let panStart = { x: 0, y: 0 };

    // Fixed viewport with border, scrollable
    const chartWrapper = el("div", {
        style: `
            position: relative;
            height: 500px;
            background: var(--bg);
            border: 2px solid var(--border);
            border-radius: 12px;
            overflow: auto;
        `
    });

    // Scalable content area (no border)
    const chartCanvas = el("div", {
        style: `
            position: relative;
            width: ${chartWidth}px;
            height: ${chartHeight}px;
            transform: scale(${zoomLevel});
            transform-origin: top left;
        `
    });

    // Update chart transform
    const updateChartTransform = () => {
        chartCanvas.style.transform = `scale(${zoomLevel})`;
    };

    // Zoom with Ctrl/Cmd + scroll wheel
    chartWrapper.addEventListener("wheel", (e: WheelEvent) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            zoomLevel = Math.max(0.25, Math.min(2.0, zoomLevel + delta));
            updateChartTransform();
        }
        // Otherwise, let native scrolling handle pan
    }, { passive: false });

    // Pan with left-click drag on empty area
    chartWrapper.addEventListener("mousedown", (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Check if clicking on empty area (wrapper or canvas background)
        const isEmptyArea = target === chartWrapper || target === chartCanvas;

        // Left-click on empty area, or middle-click anywhere
        if ((e.button === 0 && isEmptyArea) || e.button === 1) {
            e.preventDefault();
            isPanning = true;
            panStart = { x: e.clientX + chartWrapper.scrollLeft, y: e.clientY + chartWrapper.scrollTop };
            chartWrapper.style.cursor = "grabbing";
        }
    });

    chartWrapper.addEventListener("mousemove", (e: MouseEvent) => {
        if (isPanning) {
            chartWrapper.scrollLeft = panStart.x - e.clientX;
            chartWrapper.scrollTop = panStart.y - e.clientY;
        }
    });

    chartWrapper.addEventListener("mouseup", () => {
        isPanning = false;
        chartWrapper.style.cursor = "";
    });

    chartWrapper.addEventListener("mouseleave", () => {
        isPanning = false;
        chartWrapper.style.cursor = "";
    });

    // Render reference markers
    if (chart.referenceMarkers) {
        for (const marker of chart.referenceMarkers) {
            const width = marker.width ?? (marker.type === "desk" ? 100 : 40);
            const height = marker.height ?? (marker.type === "desk" ? 50 : 60);

            const markerEl = el("div", {
                style: `
                    position: absolute;
                    left: ${marker.x - minX + padding}px;
                    top: ${marker.y - minY + padding}px;
                    width: ${width}px;
                    height: ${height}px;
                    background: var(--card);
                    border: 2px solid var(--border);
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    font-size: 20px;
                    opacity: 0.8;
                `
            });
            markerEl.appendChild(el("span", {}, marker.type === "desk" ? "🖥️" : "🚪"));
            markerEl.appendChild(el("span", { style: "font-size: 9px; color: var(--muted);" },
                marker.type === "desk" ? "Desk" : "Door"));
            chartCanvas.appendChild(markerEl);
        }
    }

    // Render tables with seats
    const tableColors = getTableColors();
    for (const table of chart.tables) {
        chartCanvas.appendChild(renderTablePresentation(table, chart, students, minX, minY, padding, tableColors));
    }

    chartWrapper.appendChild(chartCanvas);
    container.appendChild(chartWrapper);

    // Zoom controls
    const controls = el("div", {
        style: "display: flex; justify-content: center; gap: 8px; margin-top: 16px;"
    });

    const zoomOutBtn = el("button", { style: "padding: 8px 16px;" }, "−");
    zoomOutBtn.addEventListener("click", () => {
        zoomLevel = Math.max(0.25, zoomLevel - 0.25);
        updateChartTransform();
    });

    const zoomDisplay = el("span", {
        style: "padding: 8px 16px; min-width: 60px; text-align: center;"
    }, `${Math.round(zoomLevel * 100)}%`);

    const zoomInBtn = el("button", { style: "padding: 8px 16px;" }, "+");
    zoomInBtn.addEventListener("click", () => {
        zoomLevel = Math.min(2.0, zoomLevel + 0.25);
        updateChartTransform();
    });

    const fitBtn = el("button", { style: "padding: 8px 16px;" }, "Fit");
    fitBtn.addEventListener("click", () => {
        zoomLevel = 1.0;
        chartWrapper.scrollLeft = 0;
        chartWrapper.scrollTop = 0;
        updateChartTransform();
    });

    controls.append(zoomOutBtn, zoomDisplay, zoomInBtn, fitBtn);
    container.appendChild(controls);

    // Update zoom display when changing
    const updateZoomDisplay = () => {
        zoomDisplay.textContent = `${Math.round(zoomLevel * 100)}%`;
    };
    chartWrapper.addEventListener("wheel", updateZoomDisplay);
    zoomOutBtn.addEventListener("click", updateZoomDisplay);
    zoomInBtn.addEventListener("click", updateZoomDisplay);
    fitBtn.addEventListener("click", updateZoomDisplay);

    return container;
}

/**
 * Render a single table for presentation mode
 */
function renderTablePresentation(
    table: Table,
    chart: SeatingChart,
    students: Student[],
    minX: number,
    minY: number,
    padding: number,
    tableColors: string[]
): HTMLElement {
    const size = getTableSize(table);
    const colorIndex = parseInt(table.id.slice(-2), 36) % tableColors.length;
    const bgColor = tableColors[colorIndex];

    const tableEl = el("div", {
        style: `
            position: absolute;
            left: ${table.x - minX + padding}px;
            top: ${table.y - minY + padding}px;
            width: ${size.width}px;
            height: ${size.height}px;
            background: ${bgColor};
            border: 2px solid var(--border);
            border-radius: ${table.shape === 'round' ? '50%' : '8px'};
            transform: rotate(${table.rotation ?? 0}deg);
            transform-origin: center center;
        `
    });

    // Render seats
    const seatPositions = calculateSeatPositions(table.shape, table.seats, size);

    for (let i = 0; i < table.seats.length; i++) {
        const seat = table.seats[i];
        const pos = seatPositions[i];
        if (!seat || !pos) continue;

        const studentId = chart.lastAssignment?.seatAssignments.get(seat.id);
        const student = studentId ? students.find(s => s.id === studentId) : null;

        const seatEl = el("div", {
            style: `
                position: absolute;
                left: ${pos.x - 18}px;
                top: ${pos.y - 18}px;
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 9px;
                font-weight: 500;
                border: 2px solid var(--border);
                background: ${student ? stringToColor(student.id) : 'var(--bg)'};
                color: ${student ? 'white' : 'var(--muted)'};
                ${!student ? 'border-style: dashed;' : ''}
            `
        });

        if (student) {
            if (student.avatarUrl) {
                seatEl.style.backgroundImage = `url(${student.avatarUrl})`;
                seatEl.style.backgroundSize = "cover";
                seatEl.style.backgroundPosition = "center";
            } else {
                seatEl.appendChild(el("span", {}, generateInitials(student.name)));
            }
            seatEl.title = student.name;
        }

        tableEl.appendChild(seatEl);
    }

    return tableEl;
}
